import type { HelpSectionsMap } from './types';

export const esHelpSections: HelpSectionsMap = {
  start: {
    title: 'Primeros pasos',
    intro: 'Guía rápida para comenzar en WERSTACK. Siga los pasos en orden para familiarizarse más rápido.',
    blocks: [
      {
        title: 'Paso 1. Elija un workspace',
        text: 'Un workspace es su proyecto con archivos, configuración y miembros del equipo propios.',
        items: [
          'En la barra lateral izquierda, abra «Propios» o «Compartidos» y seleccione un workspace.',
          'Los archivos y las carpetas cambian automáticamente.',
          '«Crear» en la parte superior de la barra lateral: un workspace personal nuevo (si no ha alcanzado el límite).',
          'Cada workspace puede tener su propio idioma, tema y color de acento.',
        ],
        where: 'Barra lateral izquierda → «Propios» / «Compartidos». Si está contraída, pase el cursor sobre el icono de una sección para ver la lista.',
      },
      {
        title: 'Paso 2. Suba sus primeros archivos',
        items: [
          'Haga clic en «Subir imágenes» en la barra de herramientas de la galería.',
          'Ctrl+Shift+V — pegue una captura de pantalla del portapapeles en la carpeta actual.',
          'Hasta 1000 archivos a la vez (JPEG, PNG, WebP; hasta 10 MB cada uno).',
          'Aparece una barra de progreso en la parte inferior; puede cerrarla o cancelarla.',
        ],
        where: 'Pantalla principal → botón «Subir imágenes» arriba a la derecha.',
      },
      {
        title: 'Paso 3. Cree carpetas',
        items: [
          'Haga clic en «Crear una carpeta», introduzca un nombre y confirme.',
          'Abra una carpeta con doble clic (un toque en dispositivos táctiles).',
          'En la raíz del workspace, la barra de herramientas muestra el nombre del workspace a la izquierda.',
          'Dentro de una subcarpeta: migas de pan con icono de inicio para volver a la raíz.',
        ],
        where: 'Pantalla principal → «Crear una carpeta» junto a la subida.',
      },
      {
        title: 'Barra lateral',
        items: [
          'Logo de WERSTACK y listas de workspaces arriba.',
          '«Crear» — workspace nuevo.',
          '«Datos» — tablas: «Todas» abre el catálogo; listas debajo para abrir rápido.',
          '«Tableros» — kanban sobre tablas.',
          '«Flujos» — grafos de automatización del espacio actual.',
          '«Galerías» — catálogo de galerías públicas.',
          'Botón en el borde: contraer o expandir la barra lateral.',
        ],
        where: 'Columna izquierda de la pantalla principal.',
      },
      {
        title: 'Botones de la barra superior',
        items: [
          'Campana — invitaciones del equipo y notificaciones de comentarios.',
          'Signo de interrogación — este panel de ayuda (independiente del perfil).',
          'Iniciales — cerrar sesión.',
        ],
        where: 'Esquina superior derecha de la pantalla.',
      },
      {
        title: 'Roles y permisos',
        text: 'No todas las acciones están disponibles para todos los usuarios.',
        items: [
          'Dueño y Admin — acceso completo; gestión de miembros y galerías.',
          'Editor — ver y editar archivos; sin gestión del equipo.',
          'Espectador — solo lectura; sin subida ni eliminación.',
        ],
        tip: 'Si un botón está deshabilitado o falta un elemento del menú, es posible que tenga el rol Espectador.',
      },
    ],
  },
  gallery: {
    title: 'Galería: archivos y carpetas',
    intro: 'Pantalla principal donde se almacenan y organizan sus imágenes.',
    blocks: [
      {
        title: 'Barra de herramientas',
        items: [
          'Izquierda — nombre del workspace (en la raíz), migas de pan (en subcarpeta) o indicador de búsqueda/filtro activo.',
          'Derecha — subida, crear carpeta, búsqueda, filtro por tags, ordenación, vista cuadrícula/lista, papelera.',
        ],
        where: 'Barra debajo del encabezado superior de la aplicación.',
      },
      {
        title: 'Modos de vista',
        items: [
          'Cuadrícula — tarjetas con vista previa, likes e indicador de comentarios.',
          'Lista — tabla: nombre, fecha, tipo, tamaño; útil para selección masiva.',
        ],
        where: 'Botón de cambio de vista (iconos de cuadrícula y lista) a la derecha de la barra de herramientas.',
      },
      {
        title: 'Búsqueda de archivos',
        items: [
          'Haga clic en el icono de la lupa e introduzca al menos 2 caracteres.',
          'La búsqueda incluye nombre de archivo, tags y texto de comentarios.',
          'Para salir de la búsqueda — haga clic en «Restablecer búsqueda» o vacíe el campo.',
        ],
        where: 'Icono de lupa en la barra de herramientas. Ordenación y subida se ocultan mientras la búsqueda está activa.',
      },
      {
        title: 'Filtro por tags',
        items: [
          'Haga clic en el icono de tag y seleccione uno o más tags.',
          'Los tags seleccionados aparecen como chips a la izquierda; elimine cualquiera con ×.',
          'Con tags activos aparece «Crear una galería por etiquetas» (véase Galerías públicas).',
        ],
        where: 'Icono de tag en la barra de herramientas.',
      },
      {
        title: 'Ordenación',
        items: [
          'Por fecha de subida (más recientes o más antiguos primero).',
          'Por nombre (A–Z o Z–A).',
          'Por tamaño de archivo.',
          'Por número de likes.',
        ],
        where: 'Icono de controles deslizantes en la barra de herramientas. No disponible en papelera, búsqueda o filtro por tags.',
      },
      {
        title: 'Likes',
        items: [
          'Haga clic en el corazón de una tarjeta para dar o quitar like.',
          'Dar like no abre el archivo — haga clic en la vista previa o el nombre para abrirlo.',
        ],
      },
      {
        title: 'Paginación',
        text: 'Si hay muchos archivos, use el panel inferior para cambiar de página y el número de elementos por página.',
        where: 'Barra de paginación en la parte inferior de la pantalla.',
      },
    ],
  },
  selection: {
    title: 'Selección y acciones',
    intro: 'Cómo seleccionar varios archivos y carpetas y qué puede hacer con ellos.',
    blocks: [
      {
        title: 'Selección con ratón',
        items: [
          'Clic en un elemento — añadir o quitar de la selección.',
          'Shift + clic — seleccionar un rango desde el último elemento seleccionado.',
          'Arrastre un recuadro en un espacio vacío — seleccione todo lo que quede dentro.',
          'Ctrl (Cmd en Mac) + recuadro — añadir a la selección actual.',
          'Ctrl+A — seleccionar todo en la página.',
          'Escape o clic fuera — limpiar la selección.',
        ],
      },
      {
        title: 'Selección táctil',
        items: [
          'Toque simple — abrir archivo o carpeta.',
          'Doble toque en una tarjeta — añadir a la selección.',
          'Arrastre en espacio vacío (elástico) — selección masiva.',
          'Pulsación larga en espacio vacío — menú contextual.',
        ],
      },
      {
        title: 'Arrastrar y soltar',
        items: [
          'Arrastre un archivo o carpeta sobre otra carpeta para moverlo.',
          'Si el elemento ya está seleccionado, se mueve toda la selección.',
          'En el teléfono, el arrastre táctil muestra una tarjeta flotante de vista previa.',
        ],
        tip: 'Arrastrar y soltar está deshabilitado en la papelera.',
      },
      {
        title: 'Cortar y pegar',
        items: [
          'Seleccione elementos → «Cortar» en el menú contextual o Ctrl+X.',
          'Vaya a la carpeta de destino → «Pegar» o Ctrl+V.',
          'No se puede pegar en la misma carpeta desde la que cortó.',
        ],
      },
      {
        title: 'Menú contextual',
        text: 'Clic derecho en la galería (o pulsación larga en táctil) para acciones.',
        items: [
          'Subir imágenes / Crear una carpeta.',
          'Cut, Paste.',
          'Rename folder (exactamente una carpeta seleccionada).',
          'Crear una galería (exactamente una carpeta seleccionada).',
          'Agregar una etiqueta (solo archivos en la selección).',
          'Generar — imágenes seleccionadas (editores y administradores).',
          'Delete (mueve a la papelera).',
          'Restaurar (solo en papelera).',
        ],
        tip: 'Las indicaciones de atajos aparecen junto a los elementos del menú.',
      },
      {
        title: 'Operaciones masivas',
        items: [
          'Delete selection — a la papelera (con confirmación).',
          'Agregar una etiqueta — a todos los archivos seleccionados a la vez.',
          'Cut and paste — mover un grupo de archivos.',
        ],
      },
      {
        title: 'Generación de imagen con IA (galería)',
        items: [
          'Seleccione una o más imágenes → menú contextual → Generar.',
          'Una imagen nueva por archivo seleccionado, una tras otra. Descargue o guarde el resultado — los originales no cambian.',
        ],
      },
    ],
  },
  viewer: {
    title: 'Visor de archivos',
    intro: 'Ventana a pantalla completa con información del archivo, comentarios y acciones.',
    blocks: [
      {
        title: 'Cómo abrir',
        items: [
          'Haga clic en una tarjeta de archivo en la galería (o en el nombre en vista de lista).',
          'En modo de selección masiva — doble clic / doble toque.',
        ],
      },
      {
        title: 'Navegar entre archivos',
        items: [
          'Flechas Anterior / Siguiente a los lados de la ventana.',
          'Teclas ← y → del teclado.',
          'Escape — cerrar (o salir de la vista de imagen a pantalla completa).',
        ],
      },
      {
        title: 'Pestaña Info',
        items: [
          'Nombre, resolución, tamaño, fecha de subida.',
          'Tags — haga clic en un tag para filtrar la galería por él.',
          'Botones: Editar, Descargar, Crear/revocar enlace público, Copiar URL.',
        ],
        where: 'Panel derecho del modal → pestaña Info.',
      },
      {
        title: 'Tags',
        items: [
          'Icono de tag en la esquina — añadir un tag nuevo.',
          '× en un chip de tag — eliminar el tag.',
        ],
      },
      {
        title: 'Generación de imagen con IA',
        items: [
          'Icono de chispas junto a Tag — describa el fondo y genere una nueva imagen del producto a partir del archivo actual.',
          'La vista previa permanece en el modal: descargue o guarde como archivo nuevo en una carpeta (el original no se sobrescribe).',
        ],
        where: 'Barra del visor → icono de chispas (editores y administradores).',
      },
      {
        title: 'Enlace público de un archivo',
        items: [
          '«Crear enlace» — cualquiera con el enlace puede verlo sin iniciar sesión.',
          '«Revocar enlace» — se revoca el acceso.',
          '«Copiar URL» — copiar enlace al portapapeles.',
        ],
        tip: 'Es un enlace de un solo archivo. Para álbumes use Galerías públicas.',
      },
      {
        title: 'Pestaña Comments',
        items: [
          'Escriba comentarios y respuestas en el archivo.',
          'Edite y elimine sus propios comentarios.',
          'Añada reacciones emoji a los comentarios.',
          'Mencione colegas con @ — empiece a escribir nombre o email.',
        ],
        where: 'Panel derecho → pestaña Comments. Las alertas de comentarios nuevos aparecen en la campana.',
      },
      {
        title: 'Vista a pantalla completa',
        text: 'Haga clic en el icono de expandir de la imagen para verla a pantalla completa.',
      },
    ],
  },
  editor: {
    title: 'Editor de imágenes',
    intro: 'Abra un archivo desde el visor con «Editar». El canvas se compone de capas: frame, imágenes, texto y formas. Todos los cambios se pueden deshacer.',
    blocks: [
      {
        title: 'Barra superior',
        items: [
          'Zoom in / out / fit to screen — escala del canvas.',
          'Undo / Redo — historial de edición.',
          'Reset — restaurar la imagen original.',
          'Se muestran el zoom actual (%) y las dimensiones del frame en píxeles.',
        ],
      },
      {
        title: 'Capas y propiedades',
        items: [
          'Panel de capas (izquierda) — lista de frame, imágenes, texto y formas; seleccionar, reordenar, bloquear, eliminar.',
          'Panel de propiedades (derecha) — ajustes de la capa seleccionada.',
          'Haga clic en una capa en el canvas o en la lista para editarla.',
          'Add image — capas de imagen adicionales desde su workspace.',
          'Ctrl+C — copiar la capa seleccionada (imagen, sticker, texto o forma). El frame no se puede copiar.',
          'Ctrl+V — pegar la capa copiada con un ligero desplazamiento.',
        ],
        where: 'Botones de la barra de herramientas con iconos de capas y controles deslizantes.',
      },
      {
        title: 'Frame (layout)',
        items: [
          'Tamaño, fondo, radio de esquinas y borde.',
          'Establecer relación de aspecto — elija un preset (1:1, 16:9, 4:3, etc.) para el marco.',
          'Al cambiar la relación de aspecto del frame, el texto y las formas permanecen en su sitio y conservan su tamaño; solo cambia el área visible del frame.',
          'La capa de imagen mantiene su posición visual; después puede redimensionarla o desplazarla como de costumbre.',
        ],
        where: 'Panel de propiedades (derecha) cuando la capa frame está seleccionada.',
        tip: 'Pulse Escape para salir del modo de relación de aspecto.',
      },
      {
        title: 'Capa de imagen',
        items: [
          'Seleccione la capa de imagen — el panel de propiedades muestra «Imagen».',
          'Posición y tamaño — campos numéricos; rotación — control deslizante (−180°…+180°) en la sección Rotación.',
          'Clic derecho en la imagen → modo redimensionar; arrastre las asas de las esquinas. Arrastre dentro de la imagen para desplazarla dentro del marco.',
          'Sección Transformación — Editar con IA (barra de prompt abajo del lienzo, hasta 200 caracteres), Quitar fondo (usa el pool compartido de tokens de IA), alinear (aparece un control deslizante de ángulo abajo; haga clic en Listo), voltear horizontal / vertical.',
          'Sección Transformación — Recortar (rectángulo libre + Cortar), Lazo (eliminar selección o mantener solo lo seleccionado + marca para aplicar).',
          'Secciones Color y Efectos — brillo, contraste, saturación y presets de filtros.',
        ],
        where: 'Panel de propiedades (derecha) cuando una capa de imagen está seleccionada.',
        tip: 'Editar con IA envía la capa tal como la ve (rotación, volteo, filtros) y la reemplaza en el lienzo. Escape sale del redimensionado, alineación, recorte, lazo o del prompt de IA. El doble toque cierra la ruta del lazo en dispositivos táctiles.',
      },
      {
        title: 'Stickers',
        items: [
          'El botón «Añadir pegatina» en la barra de herramientas abre la biblioteca de stickers de su workspace.',
          'Se crea un sticker nuevo al usar Quitar fondo en una capa de imagen — el resultado se guarda en la biblioteca y se aplica a la capa actual.',
          'Haga clic en un sticker en el panel o arrástrelo al canvas para añadirlo como capa independiente.',
          'Los stickers se comportan como imágenes: posición, tamaño, rotación, sombra; aparecen con un icono dedicado en el panel de capas.',
          'El × en una tarjeta de la biblioteca elimina permanentemente el sticker del workspace (las capas ya colocadas en el canvas no se ven afectadas).',
        ],
        where: 'Barra de herramientas → icono de sticker; panel de stickers a la izquierda (junto al panel de capas).',
        tip: 'Quitar fondo usa la capa tal como la ve, incluyendo rotación, volteo, escala y filtros.',
      },
      {
        title: 'Capas de texto',
        items: [
          'Añada una capa de encabezado o cuerpo de texto desde la barra de herramientas.',
          'Fuente, tamaño, color, trazo, sombra, fondo, espaciado.',
          'Alineación — horizontal (left / center / right) y vertical (top / center / bottom) dentro del cuadro de texto.',
          'Arrastre el texto en el canvas; redimensione el cuadro con las asas de las esquinas.',
          '«Aplicar a la imagen» — integrar el texto en el bitmap antes de guardar.',
        ],
        where: 'En móvil, abra el panel de propiedades con el icono de controles deslizantes.',
      },
      {
        title: 'Formas',
        items: [
          'Rectangle, triangle, circle, line — elija una herramienta y arrastre en el canvas para dibujar.',
          'Relleno, trazo, radio de esquinas, sombra y degradado en propiedades.',
          'Arrastre para mover; asas de esquina para redimensionar; asa superior para rotar.',
          'Escape cancela la herramienta de dibujo activa.',
        ],
      },
      {
        title: 'Navegación del canvas',
        items: [
          'Ratón: arrastre para desplazar; rueda para zoom.',
          'Táctil: pinch para zoom; un dedo para desplazar.',
        ],
      },
      {
        title: 'Guardado',
        items: [
          'Guardar — sobrescribir el archivo original.',
          'Descargar — guardar el archivo en su equipo.',
          'Guardar como — archivo nuevo (nombre, PNG/JPG/WebP, tamaño, carpeta).',
        ],
        where: 'Botón Guardar y menú desplegable a la derecha del encabezado del editor.',
      },
    ],
  },
  publicGalleries: {
    title: 'Galerías públicas',
    intro: 'Páginas atractivas para mostrar trabajo a clientes — no se requiere cuenta.',
    blocks: [
      {
        title: 'Galería desde una carpeta',
        items: [
          'Seleccione exactamente una carpeta.',
          'Menú contextual → «Crear una galería» (o Ctrl+Alt+G).',
          'Configure nombre, layout, theme e información del autor.',
          'Copie el enlace y envíelo a su cliente.',
        ],
      },
      {
        title: 'Galería por tags',
        items: [
          'Active el filtro por uno o más tags.',
          'Haga clic en «Crear una galería por etiquetas».',
          'El contenido se actualiza automáticamente cuando cambie los tags de los archivos.',
        ],
        where: 'El botón aparece en la barra de herramientas cuando el filtro por tags está activo.',
      },
      {
        title: 'Configuración de galería',
        items: [
          'Layout: grid, masonry o large cards.',
          'Theme: claro/oscuro y color de acento.',
          'Genere una tarjeta de visita con código QR.',
        ],
      },
      {
        title: 'Panel del autor',
        items: [
          'Modo «Autor» — nombre, ocupación, contactos y descripción.',
          'Modo «Empresa» — nombre, logo desde archivos del workspace, eslogan, contactos y «Sobre la empresa».',
          'Cambiar de modo no borra los datos del otro modo.',
          'Los campos completados se muestran en el panel lateral de la página pública y en la tarjeta de visita digital.',
        ],
        where: 'Sección «Autor» en el diálogo de crear o editar galería.',
      },
      {
        title: 'Gestionar galerías',
        items: [
          'Barra lateral → «Galerías» — catálogo de todas las galerías públicas por espacio.',
          'Abrir, editar, eliminar o mostrar la tarjeta QR — acciones en la tarjeta.',
        ],
        where: 'Barra lateral → «Galerías» (página /galleries).',
      },
      {
        title: 'Qué ven los visitantes',
        items: [
          'Cuadrícula de imágenes con scroll infinito.',
          'Vista a pantalla completa (Escape, ← →).',
          'Panel del autor con datos de la persona o la empresa y contactos.',
          'Descarga de tarjeta de visita digital con código QR a la galería.',
        ],
      },
    ],
  },
  profile: {
    title: 'Perfil y equipos',
    intro: 'Configuración personal, workspaces y miembros del equipo.',
    blocks: [
      {
        title: 'Abrir perfil',
        items: [
          'Elemento «Perfil» en la parte inferior de la barra lateral.',
          '«Cerrar sesión» — cerrar sesión (menú de iniciales, arriba a la derecha).',
        ],
        tip: 'El perfil se abre como página completa; la ayuda (signo de interrogación) es un panel aparte.',
      },
      {
        title: 'Pestaña Personal',
        items: [
          'Cambie nombre y email.',
          'Haga clic en «Guardar cambios».',
        ],
      },
      {
        title: 'Pestaña Tokens de IA',
        items: [
          'Muestra tokens usados y restantes del pool compartido (quitar fondo, generación de imágenes, edición con IA).',
          'Cuando se agotan los tokens, las acciones de IA quedan deshabilitadas hasta que un admin suba su límite.',
        ],
        where: 'Perfil → Tokens de IA.',
      },
      {
        title: 'Pestaña Seguridad',
        items: [
          'Cambie la contraseña: contraseña nueva y confirmación.',
          'La contraseña debe cumplir los requisitos de complejidad.',
        ],
      },
      {
        title: 'Pestaña Workspaces',
        items: [
          'Cree un workspace nuevo (nombre, idioma, theme, acento).',
          'Edite o elimine su workspace.',
          'Abandone el workspace de otra persona.',
          'Consulte el uso de almacenamiento.',
        ],
      },
      {
        title: 'Pestaña Equipos',
        items: [
          'Lista de miembros de cada uno de sus workspaces, con usados / máx. (el límite incluye al propietario).',
          'Cambie el rol: Admin, Editor, Espectador (⋮ → Editar rol).',
          'Asignar visibilidad: ocultar carpetas, tablas o tableros a un miembro (⋮ → Asignar visibilidad).',
          'Elimine un miembro del workspace.',
        ],
      },
      {
        title: 'Visibilidad',
        items: [
          'La visibilidad es una lista de denegación además del rol: un recurso oculto sigue inaccesible aunque el rol lo permita.',
          'El propietario puede ocultar recursos a cualquier miembro. El admin solo a Editor y Espectador — no al propietario ni a otro admin.',
          'Ocultar una carpeta también oculta subcarpetas y archivos en la app (listas, búsqueda, apertura por URL).',
          'Las tablas y tableros se ocultan por separado de las carpetas.',
          'Al invitar: tras elegir el rol puede definir ocultaciones opcionales antes de enviar.',
          'Los enlaces públicos de archivos siguen funcionando aunque el archivo esté en una carpeta oculta.',
          'La carpeta raíz del workspace no se puede ocultar.',
        ],
        where: 'Perfil → Equipos → ⋮ en un miembro → Asignar visibilidad. O campana → Enviado → asistente de invitación.',
        tip: 'Las ocultaciones solo aplican en ese workspace y no en otros.',
      },
    ],
  },
  datasets: {
    title: 'Tablas',
    intro: 'Tablas tipo CRM en tus espacios — columnas, filas y edición inline.',
    blocks: [
      {
        title: 'Dónde encontrar tablas',
        items: [
          'Barra lateral → « Datos » → « Todas » — catálogo de todas las tablas por espacio (página /data).',
          'Barra lateral → « Datos »: lista rápida de tus tablas y las compartidas.',
          '« Crear » bajo Datos — nueva tabla en el espacio actual.',
          'En la página del catálogo — abrir, editar, eliminar, exportar o ver el esquema desde las acciones de la tarjeta.',
        ],
        where: 'Barra lateral izquierda → Datos (y « Todas » para el catálogo completo).',
      },
      {
        title: 'Abrir una tabla',
        items: [
          'Clic en un nombre en la barra lateral o en una tarjeta en /data — cuadrícula CRM en /data/:id.',
          'Al desplazarse se cargan más filas (100 por lote).',
          'La posición de scroll se recuerda al volver a la misma tabla.',
        ],
      },
      {
        title: 'Columnas y esquema',
        items: [
          '+ en la cabecera — añadir columna (nombre, tipo, opciones).',
          'Arrastrar el asa — reordenar columnas.',
          'Arrastrar el borde derecho — cambiar ancho; se guarda localmente.',
          'Clic en el nombre de la columna — renombrar (si puedes editar el esquema).',
          'Menú ⋮ — filtro por texto, orden ascendente o descendente, fijar columna al desplazarse horizontalmente, quitar orden/filtro o eliminar columna.',
          'Catálogo /data → editar en una tarjeta — nombre, descripción y columnas.',
        ],
      },
      {
        title: 'Ordenación y filtro',
        text: 'Cada tabla recuerda una ordenación y un filtro de texto para tu vista. Se guardan en el navegador y se aplican al cargar filas — incluido el scroll infinito y la descarga.',
        items: [
          'Abrir ⋮ en la cabecera de columna (siempre visible en tablet y móvil).',
          'Orden ascendente o descendente — una flecha pequeña en el título indica la dirección.',
          'Solo se puede ordenar por una columna; «Quitar orden» en el mismo menú.',
          'Filtro — escribe texto y pulsa «Aplicar» (o Enter) para filas donde la columna contenga ese texto.',
          'Con filtro activo, junto al título aparece un icono de filtro pequeño (como en la ordenación).',
          '«Quitar filtro» para resetear; cambiar orden o filtro recarga filas del servidor.',
        ],
        where: 'Cabecera de columna → menú ⋮.',
        tip: 'Orden y filtro se guardan por tabla en este dispositivo y se usan al exportar desde el catálogo de tablas.',
      },
      {
        title: 'Listas de referencia',
        text: 'Una lista de referencia es una pequeña « tabla-diccionario » en el espacio: estados, categorías, SKU y otros valores que conviene tener en un solo sitio en lugar de copiarlos a mano.',
        items: [
          'Crear: barra lateral → « Datos » → « Crear » → activa « Crear lista de referencia ». Rellénala a mano o importa desde CSV/Excel.',
          'Dentro hay una sola columna de valores únicos. Funciona como una tabla normal: añade, edita y borra filas en la cuadrícula.',
          'Catálogo /data → lápiz en la lista: nombre, descripción, renombrar la columna clave, sección « Valores de la lista ».',
          'Ahí también puedes descargar — la exportación respeta filtro y orden si los configuraste en la cuadrícula.',
          'Importar en una lista existente: ábrela → « Importar CSV/Excel » → elige la columna del archivo con las claves; se omiten duplicados en el archivo.',
          'En la barra lateral y en la página del catálogo, las listas llevan la insignia « List ».',
        ],
        where: 'Crear: barra lateral → Datos → Crear. Gestionar: barra lateral → Datos → Todas (/data).',
        tip: 'Una lista por espacio = una fuente única para estados, temporadas, SKU y similares.',
      },
      {
        title: 'Columna « Referencia » en una tabla',
        text: 'Cuando una celda debe elegir de una lista compartida — no escribir texto libre — añade una columna tipo Referencia y enlázala con la lista que necesites.',
        items: [
          'Lista (select) — las opciones viven solo en esa columna. Útil para pocas opciones locales.',
          'Referencia — los valores vienen de una lista del espacio. Útil cuando el mismo conjunto se usa en varias tablas.',
          'Añadir columna: « + » en la cabecera o catálogo /data → editar tabla → Añadir columna → tipo Referencia → elige la lista.',
          'En la celda verás un desplegable con búsqueda. Al guardar la fila, el valor queda ligado a la entrada de la lista.',
          'Si renombras un valor en la lista, la tabla muestra el texto actualizado.',
        ],
        where: 'Cabecera de la tabla → « + », o Datos → Todas → editar en una tarjeta.',
        tip: 'Crea y rellena la lista primero; luego añade columnas Referencia en tus tablas de trabajo.',
      },
      {
        title: 'Listas y archivos',
        text: 'Puedes vincular filas de la tabla con archivos de la galería y actualizar tags automáticamente cuando cambia una columna Referencia.',
        items: [
          'Paso 1 — Vinculación con archivos: abre la tabla → « Vinculación con archivos » → elige la columna para emparejar (a menudo SKU o código) y cómo se relaciona con los tags.',
          'Después la cuadrícula muestra una columna con miniaturas de archivos vinculados; desde el visor de imágenes ves las filas relacionadas.',
          'Paso 2 — Acción sobre archivos en la columna Referencia: al crear o editar la columna, elige « Añadir tag » o « Reemplazar tag en la lista ».',
          'Se ejecuta cuando la fila ya tiene archivos vinculados y cambias esa celda — los tags se actualizan solos (nombre del tag = valor de la lista).',
          'Sin vinculación con archivos configurada antes, las acciones de tags no funcionan — configura primero la conexión tabla ↔ galería.',
        ],
        where: 'Tabla → Vinculación con archivos; acción de tags — en ajustes de la columna Referencia.',
        tip: 'Flujo típico: el SKU une la fila con fotos del producto; al cambiar el estado en la lista, los archivos se etiquetan como « En venta » o « Archivo ».',
      },
      {
        title: 'Combinar tablas',
        text: 'Combina dos tablas existentes en una vista de solo lectura. Las filas se enlazan por columnas — útil para consultar datos de otra tabla.',
        items: [
          'Barra lateral → « Datos » → « Combinar » — asistente de tres pasos.',
          'Paso 1: nombre y espacio para la tabla combinada.',
          'Paso 2: tabla principal y secundaria, luego columnas de enlace (en la secundaria, la columna de enlace debe ser única).',
          'Paso 3: elegir qué columnas de cada tabla aparecen en el resultado.',
          'Las tablas combinadas se abren como una cuadrícula normal, pero filas y esquema no se editan — solo orden de columnas, filtros y ordenación.',
          'En la barra lateral y en el catálogo /data, las tablas combinadas muestran la insignia « Merge ».',
        ],
        where: 'Barra lateral izquierda → Datos → Combinar (junto a Crear).',
        tip: 'Prepare primero ambas tablas de origen con una columna clave compartida (p. ej. SKU o ID) marcada como única en la segunda tabla.',
      },
      {
        title: 'Filas y celdas',
        items: [
          'Clic en celda para editar; Enter guarda, Escape cancela.',
          'Tab / Mayús+Tab — celda siguiente / anterior.',
          '« Añadir fila » abajo — nueva fila (borrador vacío se cancela al clic fuera).',
          'Papelera en la fila — eliminar una fila.',
          'Casillas + ⋮ en cabecera — eliminar seleccionadas en masa.',
        ],
        tip: 'Los cambios se ven al instante; la app sincroniza con el servidor en segundo plano.',
      },
      {
        title: 'Permisos',
        items: [
          'Propietario / Admin — esquema, filas y metadatos.',
          'Editor — solo filas.',
          'Visor — solo lectura.',
        ],
      },
    ],
  },
  boards: {
    title: 'Tableros',
    intro: 'Tableros Kanban sobre tus tablas: columnas desde una lista de referencia (estados), tarjetas desde las filas.',
    blocks: [
      {
        title: 'Dónde encontrar tableros',
        items: [
          'Barra lateral → « Tableros » → « Todos » — catálogo por espacio (página /boards).',
          'Barra lateral → « Tableros »: lista rápida de tus tableros y los compartidos.',
          '« Crear » bajo « Tableros » — nuevo tablero en el espacio actual.',
          'En el catálogo — abrir, editar ajustes o eliminar desde el menú de la tarjeta.',
        ],
        where: 'Barra lateral izquierda → Tableros (y « Todos » para el catálogo completo).',
      },
      {
        title: 'Crear un tablero',
        items: [
          'Paso 1: nombre, descripción opcional y espacio de trabajo.',
          'Paso 2: tabla elegible y columna de referencia para estados; elige qué valores serán columnas (al menos uno).',
          'Paso 3: campo de título y campos extra opcionales en las tarjetas.',
          'Tras crear, se abre la página /boards/:id.',
        ],
        tip: 'Solo tablas normales con columna de referencia. Merge y list no aparecen en el asistente.',
      },
      {
        title: 'Tablero Kanban',
        items: [
          'Las columnas siguen el orden de estados; arrastra el asa para reordenar (admin).',
          'Las tarjetas muestran el título y algunos campos de vista previa.',
          'Un icono de mensaje con contador aparece en la tarjeta si hay comentarios.',
          'Un icono de clip con contador aparece si hay archivos adjuntos.',
          'Arrastra tarjetas entre columnas o dentro de una — cambian estado y orden.',
          'Actualizaciones en vivo por SSE — otros usuarios ven movimientos, ediciones, comentarios y adjuntar/desadjuntar archivos sin recargar.',
        ],
      },
      {
        title: 'Tarjetas',
        items: [
          'Clic en una tarjeta — modal de vista con campos, pestaña Comentarios y pestaña Archivos.',
          '⋮ → Editar — panel lateral de la fila.',
          '⋮ → Eliminar — quita la fila tras confirmar.',
          '« Añadir tarjeta » bajo una columna — nueva fila con ese estado.',
        ],
        where: 'Página del tablero → tarjeta o pie de columna.',
        tip: 'El enlace /boards/:id?card=:rowId abre el mismo modal (p. ej. desde la campana).',
      },
      {
        title: 'Comentarios de tarjeta',
        items: [
          'Los comentarios pertenecen a este tablero + tarjeta (fila), no a todos los tableros de la misma fila.',
          'Cualquiera con acceso al workspace (viewer+) puede comentar; el autor o un admin puede borrar comentarios ajenos.',
          'Los comentarios nuevos notifican a los miembros en la campana.',
        ],
      },
      {
        title: 'Archivos en tarjetas',
        items: [
          'Pestaña Archivos — adjunta imágenes del workspace del tablero (el mismo selector que en el editor de imágenes).',
          'Los adjuntos pertenecen a este tablero + tarjeta (fila), no a todos los tableros de la misma fila.',
          'Clic en la vista previa abre el visor; al pasar el cursor se ve el nombre; ✕ desadjunta sin borrar el archivo de la galería.',
          'Si el archivo está en la Papelera, el adjunto permanece con overlay «en papelera»; el borrado definitivo elimina el adjunto.',
          'Cualquiera con acceso al workspace (viewer+) puede adjuntar y desadjuntar.',
        ],
        where: 'Modal de tarjeta → pestaña Archivos.',
      },
      {
        title: 'Columnas',
        items: [
          'Admin: añadir columna (valor de estado nuevo o existente), renombrar, reordenar o quitar.',
          'Una columna con tarjetas no se puede quitar — mueve o elimina las tarjetas antes.',
          'Quitar una columna solo la oculta en este tablero; el valor permanece en la lista de referencia.',
        ],
      },
      {
        title: 'Asignados y plazos',
        items: [
          'Abra una tarjeta → Asignados: añada miembros del workspace, cada uno con su propio plazo y columna objetivo.',
          'Solo Propietario / Admin pueden asignar, cambiar plazos/columnas o definir prioridad (de la más baja a la más alta).',
          'Editor ve a todos los asignados; Visor solo ve su propia asignación en la tarjeta.',
          'Mover la tarjeta a la columna objetivo de un asignado completa su tarea; devolverla la reabre.',
          'Varias personas con la misma columna objetivo se completan con un solo movimiento.',
          'Las asignaciones vencidas se marcan en la tarjeta; las alertas llegan a la campana al iniciar sesión / refrescar el token (sin cron).',
          'Alertas personales por sus plazos (pronto / hoy / vencido). Propietario/Admin también reciben un digest si otra persona está vencida.',
        ],
        where: 'Tablero → modal de tarjeta → Asignados (y Prioridad bajo los campos).',
        tip: 'Cambiar el plazo reinicia el estado de alertas — el siguiente login/refresh podrá notificar de nuevo.',
      },
      {
        title: 'Permisos',
        items: [
          'Visor — ver el tablero, arrastrar tarjetas, comentar y adjuntar/desadjuntar archivos; solo ve sus propios asignados.',
          'Editor — crear, editar y eliminar tarjetas (filas); ve a todos los asignados pero no puede asignar.',
          'Propietario / Admin — ajustes, columnas, eliminar tablero, moderar comentarios, asignaciones, plazos/columnas y prioridad.',
        ],
      },
    ],
  },
  workflows: {
    title: 'Flujos',
    intro: 'Los flujos son una capa de producto junto a Archivos, Datos, Tableros y Galerías — no una pantalla de ajustes. Construyes un grafo de automatización que mueve archivos, filas y tarjetas por sistemas ya configurados.',
    blocks: [
      {
        title: 'No son ajustes',
        items: [
          'Los flujos viven en su propia sección de la barra lateral — como Datos o Tableros, no bajo Perfil / ajustes.',
          'Cada proceso pertenece a un espacio y usa sus carpetas, tablas, tableros, patrones y etiquetas.',
          'Activar enciende el proceso; Guardar escribe el grafo. Las ejecuciones muestran lo ocurrido.',
        ],
        tip: 'Idioma, tema y cuenta están en Perfil, no en Flujos.',
      },
      {
        title: 'Dónde encontrar los flujos',
        items: [
          'Barra lateral → «Flujos» → «Todos» — catálogo por espacio (/workflows).',
          'Barra lateral → «Flujos» → «Actuales» — lista rápida del space activo (oculta si está vacía).',
          '«Crear» bajo Flujos — grafo nuevo (solo owner/admin).',
          'Abrir un proceso — constructor de grafo; Ejecuciones — diario.',
        ],
        where: 'Barra lateral izquierda → Flujos.',
      },
      {
        title: 'Constructor de grafo',
        items: [
          'Paleta — grupos: Disparadores, Archivos, Contenido, AI, Tabla, Tablero, Flow; más panel de Plantillas.',
          'Lienzo — colocar nodos, conectar, mover; Deshacer / Rehacer junto al título (también Ctrl+Z / Ctrl+Y).',
          'Inspector — configurar el disparador o nodo seleccionado (carpeta, etiquetas, tabla, cron, etc.).',
          'Exactamente un disparador. Suele ser una cadena; If / rama puede salir a True y False.',
          '«Validar» revisa campos obligatorios y el flujo (archivos / filas) antes de Guardar.',
          'Teclado: copiar / pegar nodos (Ctrl+C / Ctrl+V), eliminar selección (Delete / Backspace) — ver Atajos.',
        ],
        tip: 'Cron: una vez, cada día, cada semana (día + hora) o cada mes (fecha + hora), con zona horaria.',
      },
      {
        title: 'Paleta: Disparadores',
        text: 'Exactamente un disparador por proceso.',
        items: [
          'Archivo subido — arranca al subir un archivo a la carpeta vigilada.',
          'Fila añadida — arranca al crear una fila en la tabla.',
          'Filas importadas — arranca tras importar CSV/Excel.',
          'Cell matched — arranca cuando una celda pasa al valor buscado.',
          'File moved — arranca al mover un archivo a la carpeta vigilada.',
          'On schedule — arranca por cron (el run empieza vacío).',
        ],
      },
      {
        title: 'Paleta: Archivos',
        items: [
          'Aplicar etiquetas — añade etiquetas a los archivos del flujo.',
          'Quitar etiquetas — quita etiquetas de los archivos del flujo.',
          'Mover archivo — mueve archivos a la carpeta destino.',
          'A la papelera — envía archivos a la papelera.',
          'Linked rows — encuentra filas de tabla vinculadas a los archivos del flujo.',
        ],
      },
      {
        title: 'Paleta: Contenido',
        items: [
          'Apply pattern — aplica un patrón del espacio a la escena de cada archivo.',
          'Save as — guarda una copia de cada archivo en la carpeta destino.',
          'Replace content — sustituye cada archivo por el recorte de Remove background.',
        ],
      },
      {
        title: 'Paleta: AI',
        items: [
          'Remove background — quita el fondo de cada imagen (tokens AI).',
          'Generate image — una imagen nueva por cada archivo del flujo.',
        ],
        tip: 'Los nodos AI usan el pool compartido de tokens del perfil.',
      },
      {
        title: 'Paleta: Tabla',
        items: [
          'Establecer celdas — escribe valores en columnas elegidas de las filas del flujo.',
          'Insert row — añade una fila nueva a la tabla.',
          'Linked files — carga en el stream los archivos vinculados a las filas.',
        ],
      },
      {
        title: 'Paleta: Tablero',
        items: [
          'Add priority — pone prioridad en las tarjetas del flujo.',
          'Clear priority — quita la prioridad de las tarjetas del flujo.',
          'Añadir asignación — asigna un miembro (con plazo/columna) a las tarjetas.',
          'Unassign — quita asignados de las tarjetas del flujo.',
        ],
      },
      {
        title: 'Paleta: Flow',
        items: [
          'Table source — alimenta filas de tabla a Generate image (entrada data).',
          'Search — carga archivos del espacio al flujo por etiquetas, nombre o extensión.',
          'Filter — deja solo los elementos del flujo que cumplen el criterio.',
          'Link — empareja archivos con valores de diccionario por nombre (etiqueta opcional).',
          'Limit — deja solo los primeros N elementos del flujo.',
          'If / branch — envía el run a True o False sin cambiar el stream.',
        ],
      },
      {
        title: 'Quién puede qué (solo lectura para editor)',
        items: [
          'Owner / Admin — crear, editar, guardar, activar/desactivar, eliminar y gestionar plantillas.',
          'Editor / Visor — catálogo y grafo solo lectura (sin paleta ni Guardar), más Ejecuciones.',
        ],
        tip: 'Los editores pueden estudiar el grafo y las ejecuciones — sin cambiar la automatización.',
      },
      {
        title: 'Ejecuciones / diario',
        items: [
          'Cada ejecución registra el disparador y cada paso: ok, skipped o failed, con tiempo y detalles.',
          'Abrir una ejecución — grafo con badges de pasos y lista de pasos.',
          'Las ejecuciones son visibles para cualquier miembro del espacio.',
        ],
        where: 'Proceso → Ejecuciones, o tarjeta del catálogo → Runs.',
      },
      {
        title: 'Plantillas de usuario',
        items: [
          'Owner / Admin: menú Guardar → «Guardar como plantilla» — grafo en la biblioteca del espacio.',
          'Panel de plantillas (izquierda) — aplicar al lienzo (copia con ids nuevos) o eliminar.',
          'Aplicar no deja enlace en vivo — editas la copia como cualquier proceso.',
        ],
        tip: 'Las plantillas son por espacio. Los editores no pueden crearlas ni aplicarlas.',
      },
    ],
  },
  notifications: {
    title: 'Notificaciones e invitaciones',
    intro: 'La campana en la barra superior es su bandeja de eventos del equipo y comentarios.',
    blocks: [
      {
        title: 'Pestaña Comments',
        items: [
          'Alertas de comentarios y respuestas nuevos en sus archivos.',
          'Alertas de comentarios en tarjetas de tablero.',
          'Plazos: pronto (24 h), hoy, vencido — si usted es asignado.',
          'Digest Propietario/Admin: otra persona en sus tableros está vencida (aprox. una vez al día por asignación).',
          'Clic en una alerta de archivo — abre la imagen y la pestaña Comentarios.',
          'Clic en una alerta de tarjeta o plazo — abre /boards/:id?card=:rowId con el modal.',
          '«Borrar todo» — eliminar todas las notificaciones del workspace actual.',
        ],
        where: 'Campana → pestaña con icono de mensaje (se abre por defecto).',
      },
      {
        title: 'Pestaña Recibidas',
        items: [
          'Invitaciones para unirse al workspace de otra persona.',
          'Acepte o rechace.',
          'Muestra: quién invitó, nombre del workspace, rol, caducidad.',
        ],
      },
      {
        title: 'Pestaña Enviadas',
        items: [
          'Invitaciones que usted envió.',
          'Cancele una invitación pendiente.',
          '«Invitar» — invite a un usuario nuevo.',
        ],
      },
      {
        title: 'Cómo invitar a un usuario',
        items: [
          'Campana → Enviado → «Invitar».',
          'Paso 1: busque al usuario por email.',
          'Paso 2: elija workspace y rol (Admin / Editor / Espectador). Las invitaciones pendientes también ocupan plaza.',
          'Si el equipo del espacio está completo, invitar queda deshabilitado hasta que un admin del sistema suba su límite.',
          'Paso 3: opcionalmente oculte carpetas, tablas o tableros y luego envíe la invitación.',
        ],
      },
      {
        title: 'Insignia en la campana',
        text: 'La insignia roja muestra el total de invitaciones pendientes y alertas de comentarios del workspace actual.',
      },
    ],
  },
  trash: {
    title: 'Papelera',
    intro: 'Los archivos y carpetas eliminados van aquí en lugar de desaparecer de inmediato.',
    blocks: [
      {
        title: 'Cómo abrir',
        items: [
          'Haga clic en «Papelera» en la barra de herramientas de la pantalla principal.',
          'Botón Atrás en el encabezado de la papelera — volver a la galería.',
        ],
        where: 'Icono de papelera a la derecha de la barra de herramientas.',
      },
      {
        title: 'Restaurar',
        items: [
          'Seleccione elementos → menú contextual → «Restaurar».',
          'Atajo: Ctrl+Alt+R.',
          'Los elementos vuelven a su ubicación original.',
        ],
      },
      {
        title: 'Eliminar permanentemente',
        items: [
          '× en una tarjeta — eliminar un archivo o carpeta para siempre (solo el dueño).',
          '«Vaciar la papelera» — eliminar todo (con confirmación, solo el dueño).',
        ],
        tip: 'Tras vaciar la papelera, los archivos no se pueden recuperar.',
      },
      {
        title: 'Limitaciones de la papelera',
        items: [
          'No se pueden subir archivos ni crear carpetas.',
          'No se puede arrastrar y soltar elementos.',
          'La ordenación no está disponible.',
        ],
      },
    ],
  },
  shortcuts: {
    title: 'Atajos y gestos',
    intro: 'Referencia rápida. Pulse Shift+? para abrir esta hoja de ayuda en cualquier momento. Los atajos se deshabilitan cuando el editor está abierto o el foco está en un campo de entrada.',
    blocks: [
      {
        title: 'Galería',
        items: [
          'Ctrl+A — seleccionar todo.',
          'Ctrl+U — subir archivos.',
          'Ctrl+Shift+V — pegar captura de pantalla del portapapeles.',
          'Ctrl+Alt+N — crear carpeta.',
          'Ctrl+X — cortar.',
          'Ctrl+V — pegar.',
          'F2 — renombrar carpeta (una seleccionada).',
          'Ctrl+Alt+G — crear galería (una carpeta).',
          'Ctrl+Alt+T — añadir tag.',
          'Delete — mover a la papelera.',
          'Ctrl+Alt+R — restaurar (en papelera).',
          'Escape — limpiar selección.',
        ],
      },
      {
        title: 'Visor de archivos',
        items: [
          '← / → — archivo anterior / siguiente.',
          'Escape — cerrar ventana.',
        ],
      },
      {
        title: 'Editor',
        items: [
          'Ctrl+Z — deshacer.',
          'Ctrl+Y — rehacer.',
          'Ctrl+0 — ajustar a pantalla.',
          'Ctrl++ — acercar.',
          'Ctrl+- — alejar.',
          'H — voltear horizontal (imagen seleccionada).',
          'V — voltear vertical (imagen seleccionada).',
          'T — modo alinear.',
          'C — recorte libre.',
          'L — lazo: eliminar selección.',
          'Shift+L — lazo: mantener solo lo seleccionado.',
          'Ctrl+C — copiar capa seleccionada (no el frame).',
          'Ctrl+V — pegar capa.',
          'Delete / Backspace — eliminar capa seleccionada (texto, imagen o forma).',
          'Escape — salir del modo actual (relación de aspecto, redimensionar, herramienta de forma, recorte, lazo) o restablecer cambios.',
        ],
        tip: 'Las indicaciones de atajos aparecen en los tooltips de los botones del editor (en escritorio con ratón). La relación de aspecto del marco y las herramientas de capa no tienen atajos de letra — use el panel de propiedades.',
      },
      {
        title: 'Tablas (cuadrícula CRM)',
        items: [
          'Enter — guardar celda o empezar a editar.',
          'Escape — cancelar edición de celda.',
          'Tab / Mayús+Tab — celda siguiente / anterior.',
        ],
        where: 'Abre cualquier tabla desde la barra lateral.',
      },
      {
        title: 'Editor de procesos (flujos)',
        items: [
          'Ctrl+Z — deshacer.',
          'Ctrl+Y o Ctrl+Mayús+Z — rehacer.',
          'Ctrl+C — copiar nodos seleccionados.',
          'Ctrl+V — pegar nodos.',
          'Delete / Backspace — eliminar nodos seleccionados.',
        ],
        where: 'Abre un proceso desde la barra lateral (Flujos).',
        tip: 'Los atajos usan teclas físicas (valen en teclados no latinos). Desactivados si el foco está en un campo o el proceso es de solo lectura.',
      },
      {
        title: 'Gestos táctiles',
        items: [
          'Toque simple — abrir; doble toque — seleccionar.',
          'Rubber band — selección masiva.',
          'Pulsación larga — menú contextual.',
          'Pinch en el editor — zoom.',
        ],
      },
    ],
  },
};
